// Derived "is section N unlocked / complete?" state for the Underwriting
// tab, so the accordion can disable locked sections + auto-expand the
// first incomplete one. The section order and its locks exist only here:
// the server checks each action's own preconditions, never the order.
//
// Sections are numbered by display position. Section 1 is always unlocked;
// each later section unlocks once the section above it is complete.
//   1 Pre-Underwriting Approval → all 4 UW.S1 certs signed.
//   2 Escrow Setup → the legacy escrow row has IssuedToBuyerAt +
//        FundingVerifiedAt, or (no legacy row) the deal's escrow
//        transaction has reached Buyer Funded.
//   3 Underwriting Team Appointment → A2/A3/A4 each Assigned or Not
//        Applicable.
//   4 A1 Criteria Checklist → every A1 checklist item is terminal.
//   5 A1 Pass / Fail + DQR → a LaneAssessment for A1 exists.
//   6 Verification of Analyst Work → each non-N/A lane has its
//        UW.S7.Verify{lane} cert signed (persisted as section 7).
//   7 Loan Document Issuance → both the Buyer and Seller bundles have
//        Issued + Acknowledged dates (no certs — plain record).
//   8 Approval / Rejection Letter → UW.S8.LetterIssued signed.
//   9 Closing Coordinator + Hand-off → UW.S9.HandOffToClosing signed.

import { useMemo } from "react";
import {
  useCriteriaChecklistByLane,
  useDealAssignments,
  useDealEscrowTransaction,
  useDealOutcome,
  useEscrowSetup,
  useLaneAssessments,
  useLoanDocumentIssuance,
  useUnderwritingCertifications,
} from "@/hooks/dealFlow/useDealFlow";
import type {
  CriteriaChecklistItemDto,
  DealAssignmentDto,
  UnderwritingCertificationDto,
} from "@/api/dealFlow/types";

export interface UnderwritingTabState {
  section1Complete: boolean;
  section2Complete: boolean;
  section3Complete: boolean;
  section4Complete: boolean;
  section5Complete: boolean;
  section6Complete: boolean;
  section7Complete: boolean;
  section8Complete: boolean;
  section9Complete: boolean;
  /** Section 1 .. 9 keyed; true ⇒ user may interact with the section. */
  unlocked: Record<number, boolean>;
  /** Lowest-numbered section that is unlocked but incomplete — used to
   *  drive accordion defaultValue. */
  firstIncompleteSection: number;
  isLoading: boolean;
}

export function useUnderwritingTabState(
  dealId: string | undefined,
): UnderwritingTabState {
  const s1Certs = useUnderwritingCertifications(dealId, "Underwriting", 1);
  const s3Certs = useUnderwritingCertifications(dealId, "Underwriting", 3);
  const s7Certs = useUnderwritingCertifications(dealId, "Underwriting", 7);
  const s8Certs = useUnderwritingCertifications(dealId, "Underwriting", 8);
  const s9Certs = useUnderwritingCertifications(dealId, "Underwriting", 9);
  const escrow = useEscrowSetup(dealId);
  // No legacy EscrowSetup row for this deal (new model) — Section 2 derives
  // completion from the linked EscrowTransaction's lifecycle instead. Only
  // enabled once we know there's no legacy row, to avoid a needless fetch on
  // legacy-model deals.
  const escrowIsNewModel = !escrow.isLoading && escrow.data == null;
  const newModelEscrow = useDealEscrowTransaction(
    escrowIsNewModel ? dealId : undefined,
  );
  const loanDocIssuance = useLoanDocumentIssuance(dealId);
  const assignments = useDealAssignments(dealId);
  const checklistA1 = useCriteriaChecklistByLane(dealId, "A1");
  const laneAssessments = useLaneAssessments(dealId);
  // outcome is queried so its data is available to downstream sections;
  // section gates themselves derive from the more granular sources.
  useDealOutcome(dealId);

  return useMemo(() => {
    const isLoading =
      s1Certs.isLoading ||
      s3Certs.isLoading ||
      s7Certs.isLoading ||
      s8Certs.isLoading ||
      s9Certs.isLoading ||
      escrow.isLoading ||
      (escrowIsNewModel && newModelEscrow.isLoading) ||
      loanDocIssuance.isLoading ||
      assignments.isLoading ||
      checklistA1.isLoading ||
      laneAssessments.isLoading;

    const certsMap = (rows: UnderwritingCertificationDto[] | undefined) => {
      const m = new Set<string>();
      for (const r of rows ?? []) m.add(r.certificationCode);
      return m;
    };

    // Each "…Done" flag stays bound to its data source — NOT to a display
    // position. Certification codes (UW.S7.* etc.) and the persisted
    // UnderwritingCertification.Section integers are intentionally frozen.
    // Display position is applied below, in completeByPosition, so position and
    // data identity stay decoupled.
    const s1Set = certsMap(s1Certs.data);
    const preUwDone =
      s1Set.has("UW.S1.IntakePackageReceived") &&
      s1Set.has("UW.S1.BLAReadyForIssuance") &&
      s1Set.has("UW.S1.AssignedTeamConfirmed") &&
      s1Set.has("UW.S1.PreUnderwritingReady");

    // Loan Document Issuance (display 7) is now a plain date record with no
    // certifications — complete once BOTH bundles have Issued + Acknowledged.
    const loanDocDone =
      !!loanDocIssuance.data?.issuedAt &&
      !!loanDocIssuance.data?.acknowledgedAt &&
      !!loanDocIssuance.data?.sellerIssuedAt &&
      !!loanDocIssuance.data?.sellerAcknowledgedAt;

    const escrowDone = escrow.data
      ? !!escrow.data.issuedToBuyerAt && !!escrow.data.fundingVerifiedAt
      : !!newModelEscrow.transaction?.lifecycle.buyerFunded.reached;

    const assignmentByRole = byRole(assignments.data);
    const teamApptDone = ["A2", "A3", "A4"].every((r) => {
      const a = assignmentByRole.get(r);
      return a?.status === "Assigned" || a?.status === "NotApplicable";
    });

    const items: CriteriaChecklistItemDto[] = checklistA1.data ?? [];
    const criteriaDone =
      items.length > 0 &&
      items.every(
        (i) =>
          i.status === "Approved" ||
          i.status === "Declined" ||
          i.status === "Waived",
      );

    const lanePassFailDone =
      (laneAssessments.data ?? []).some((a) => a.lane === "A1");

    const s7Set = certsMap(s7Certs.data);
    const verificationDone = (["A2", "A3", "A4"] as const).every((lane) => {
      const na = assignmentByRole.get(lane)?.status === "NotApplicable";
      if (na) return true;
      return s7Set.has(`UW.S7.Verify${lane}`);
    });

    const s8Set = certsMap(s8Certs.data);
    const letterDone = s8Set.has("UW.S8.LetterIssued");

    const s9Set = certsMap(s9Certs.data);
    const handOffDone = s9Set.has("UW.S9.HandOffToClosing");

    // Display order (Loan-Doc moved from position 2 to position 7). Index i is
    // display position i+1. The unlock chain and firstIncomplete walk this
    // array, so a section unlocks only when the section physically above it is
    // complete — regardless of which data source backs it.
    const completeByPosition: boolean[] = [
      preUwDone, // 1 — Pre-Underwriting Approval
      escrowDone, // 2 — Escrow Setup
      teamApptDone, // 3 — Underwriting Team Appointment
      criteriaDone, // 4 — A1 Criteria Checklist
      lanePassFailDone, // 5 — A1 Pass / Fail + DQR
      verificationDone, // 6 — Verification of Analyst Work
      loanDocDone, // 7 — Loan Document Issuance
      letterDone, // 8 — Approval / Rejection Letter
      handOffDone, // 9 — Closing Coordinator + Hand-off
    ];

    const unlocked: Record<number, boolean> = { 1: true };
    for (let i = 2; i <= 9; i++) unlocked[i] = completeByPosition[i - 2];

    let firstIncomplete = 1;
    for (let i = 1; i <= 9; i++) {
      const complete = completeByPosition[i - 1];
      if (!complete && unlocked[i]) {
        firstIncomplete = i;
        break;
      }
      if (i === 9 && complete) {
        firstIncomplete = 9;
      }
    }

    return {
      section1Complete: completeByPosition[0],
      section2Complete: completeByPosition[1],
      section3Complete: completeByPosition[2],
      section4Complete: completeByPosition[3],
      section5Complete: completeByPosition[4],
      section6Complete: completeByPosition[5],
      section7Complete: completeByPosition[6],
      section8Complete: completeByPosition[7],
      section9Complete: completeByPosition[8],
      unlocked,
      firstIncompleteSection: firstIncomplete,
      isLoading,
    };
  }, [
    s1Certs.data,
    s1Certs.isLoading,
    s3Certs.isLoading,
    s7Certs.data,
    s7Certs.isLoading,
    s8Certs.data,
    s8Certs.isLoading,
    s9Certs.data,
    s9Certs.isLoading,
    escrow.data,
    escrow.isLoading,
    newModelEscrow.transaction,
    newModelEscrow.isLoading,
    loanDocIssuance.data,
    loanDocIssuance.isLoading,
    assignments.data,
    assignments.isLoading,
    checklistA1.data,
    checklistA1.isLoading,
    laneAssessments.data,
    laneAssessments.isLoading,
  ]);
}

function byRole(rows: DealAssignmentDto[] | undefined) {
  const map = new Map<string, DealAssignmentDto>();
  for (const r of rows ?? []) map.set(r.roleCode, r);
  return map;
}
